import React, { useState, useEffect } from 'react';
import { ApplianceDTO, NetworkIPSecDTO, NetworkLanDTO, NetworkWanDTO } from '@webip/shared/src/dto/appliance';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import { mdiPlus, mdiPencil, mdiTrashCan } from '@mdi/js';
import Modal from '@/components/layout/Modal';
import { FormNetwork } from './FormNetwork';
import { FormIPSec } from './FormIPSec';
import { LoadBalancingPanel } from './LoadBalancingPanel';
import { useMutation, useQuery } from 'react-query';
import axios from 'axios';
import { toastError, toastSuccess } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import BotaoExcluirComCascata from '@/components/BotaoExcluirComCascata';
import Card from '@/components/layout/Card';
import StatusDot from '@/components/layout/StatusDot';
import ConnectionLogPanel from '@/components/layout/ConnectionLogPanel';
import type { IPSecStatusDTO, WanStatusDTO, LanStatusDTO } from '@webip/shared/src/dto/connection_status';
import { formatDuration } from '@/lib/formatDuration';

type Props = {
    appliance: ApplianceDTO;
    onClose: () => void;
    onRefresh: () => void;
    onRegisterAdd?: (fn: () => void) => void;
};

type NetworkType = 'lan' | 'wan' | 'ipsec' | null;

type ApiResponse = {
    ok: boolean;
    msg?: string;
    fieldErrors?: Record<string, string>;
};

export const NetworksManager = ({ appliance, onRefresh, onRegisterAdd }: Props) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingType, setEditingType] = useState<NetworkType>(null);
    const [editingNetwork, setEditingNetwork] = useState<NetworkLanDTO | NetworkWanDTO | null>(null);
    const [editingIPSec, setEditingIPSec] = useState<NetworkIPSecDTO | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const [availablePorts, setAvailablePorts] = useState<string[]>([]);

    const queryIPSecStatus = useQuery({
        queryKey: ['ipsec-status', appliance.id],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; agentOnline: boolean; result: IPSecStatusDTO[] }>(
                `/api/ipsec/status?appliance_id=${appliance.id}`,
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        enabled: !!appliance.id && (appliance.ipsecs?.length ?? 0) > 0,
        refetchInterval: 15_000,
    });

    const ipsecStatusMap = new Map<number, IPSecStatusDTO>(
        (queryIPSecStatus.data ?? []).map((s) => [s.ipsec_id, s]),
    );

    const queryWanStatus = useQuery({
        queryKey: ['wan-status', appliance.id],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; agentOnline: boolean; result: WanStatusDTO[] }>(
                `/api/rede_wan/status?appliance_id=${appliance.id}`,
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        enabled: !!appliance.id && (appliance.redesWan?.length ?? 0) > 0,
        refetchInterval: 15_000,
    });

    const wanStatusMap = new Map<number, WanStatusDTO>(
        (queryWanStatus.data ?? []).map((s) => [s.wan_id, s]),
    );

    const queryLanStatus = useQuery({
        queryKey: ['lan-status', appliance.id],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; agentOnline: boolean; result: LanStatusDTO[] }>(
                `/api/rede_lan/status?appliance_id=${appliance.id}`,
                { withCredentials: true },
            );
            return res.data.result ?? [];
        },
        enabled: !!appliance.id && (appliance.redesLan?.length ?? 0) > 0,
        refetchInterval: 15_000,
    });

    const lanStatusMap = new Map<number, LanStatusDTO>(
        (queryLanStatus.data ?? []).map((s) => [s.lan_id, s]),
    );

    useEffect(() => {
        // Calcular portas disponíveis (excluindo a rede em edição)
        const usedPorts = [
            ...(appliance.redesLan?.flatMap(lan =>
                editingNetwork && editingType === 'lan' && lan.id === editingNetwork.id ? [] : lan.portas
            ) || []),
            ...(appliance.redesWan?.flatMap(wan =>
                editingNetwork && editingType === 'wan' && wan.id === editingNetwork.id ? [] : wan.portas
            ) || []),
        ];

        const allPorts = appliance.portas || [];

        // Redes VLAN vivem sobre uma interface-base (trunk) que pode ser
        // compartilhada com a rede nativa e com outras VLANs. Nesse caso a
        // lista de portas = portas livres ∪ trunks já usados por outra rede
        // (ou seja, todas as físicas). Redes dedicadas (sem VLAN) mantêm o
        // comportamento exclusivo: apenas portas ainda não usadas.
        const isVlan = (editingNetwork?.vlan ?? 0) > 0;
        const available = isVlan
            ? allPorts
            : allPorts.filter(port => !usedPorts.includes(port));

        setAvailablePorts(available);
    }, [appliance, editingNetwork, editingType]);

    const mutSaveLan = useMutation({
        mutationFn: async (network: NetworkLanDTO) => {
            const data = { ...network, appliance_id: appliance.id };
            // Rede base só existe com VLAN (o campo some da tela quando ela é zerada).
            if (!((data.vlan ?? 0) > 0)) data.lan_base_id = null;
            if (network.id && network.id > 0) {
                return await axios.put<ApiResponse>(`/api/rede_lan/${network.id}`, data);
            } else {
                return await axios.post<ApiResponse>('/api/rede_lan', data);
            }
        },
        onSuccess: () => {
            toastSuccess('Rede LAN salva com sucesso!');
            onRefresh();
            handleCloseEditModal();
        },
        onError: (error: unknown) => {
            const { message, fieldErrors: errs } = extractApiError(error, 'Erro ao salvar rede LAN');
            if (errs) setFieldErrors(errs);
            toastError(message);
        },
    });

    const mutSaveWan = useMutation({
        mutationFn: async (network: NetworkWanDTO) => {
            const data = { ...network, appliance_id: appliance.id };
            if (network.id && network.id > 0) {
                return await axios.put<ApiResponse>(`/api/rede_wan/${network.id}`, data);
            } else {
                return await axios.post<ApiResponse>('/api/rede_wan', data);
            }
        },
        onSuccess: () => {
            toastSuccess('Rede WAN salva com sucesso!');
            onRefresh();
            handleCloseEditModal();
        },
        onError: (error: unknown) => {
            const { message, fieldErrors: errs } = extractApiError(error, 'Erro ao salvar rede WAN');
            if (errs) setFieldErrors(errs);
            toastError(message);
        },
    });

    const mutSaveIPSec = useMutation({
        mutationFn: async (ipsec: NetworkIPSecDTO) => {
            const data = { ...ipsec, appliance_id: appliance.id };
            if (ipsec.id && ipsec.id > 0) {
                return await axios.put<ApiResponse>(`/api/ipsec/${ipsec.id}`, data);
            } else {
                return await axios.post<ApiResponse>('/api/ipsec', data);
            }
        },
        onSuccess: () => {
            toastSuccess('IPSec salvo com sucesso!');
            onRefresh();
            handleCloseEditModal();
        },
        onError: (error: unknown) => {
            const { message, fieldErrors: errs } = extractApiError(error, 'Erro ao salvar IPSec');
            if (errs) setFieldErrors(errs);
            toastError(message);
        },
    });

    const mutDeleteIPSec = useMutation({
        mutationFn: async (id: number) => {
            return await axios.delete(`/api/ipsec/${id}`);
        },
        onSuccess: () => {
            toastSuccess('IPSec removido com sucesso!');
            onRefresh();
        },
        onError: (error: unknown) => {
            const { message } = extractApiError(error, 'Erro ao remover IPSec');
            toastError(message);
        },
    });

    const handleAddLan = () => {
        setIsEditModalOpen(true);
        setEditingType('lan');
        setEditingNetwork({
            appliance_id: appliance.id,
            nome: '',
            tipo: 'lan',
            tipoLan: 'lan',
            portas: [],
            ips: [''],
        } as NetworkLanDTO);
        setFieldErrors({});
    };

    const handleAddWan = () => {
        setIsEditModalOpen(true);
        setEditingType('wan');
        setEditingNetwork({
            appliance_id: appliance.id,
            nome: '',
            tipo: 'wan',
            tipoWan: 'dhcp',
            uso: 'primario',
            prioridade: 0,
            peso: 1,
            portas: [],
        } as NetworkWanDTO);
        setFieldErrors({});
    };

    const handleAddIPSec = () => {
        setIsEditModalOpen(true);
        setEditingType('ipsec');
        setEditingIPSec({
            appliance_id: appliance.id,
            nome: '',
            tipo: 'ipsec',
            ipsTeste: [],
            fase1: {
                tipo: 'ikev2',
                enderecoLocal: '',
                enderecosRemotos: [],
                auth: 'psk',
                psk: '',
                dhGroup: 14,
                lifetime: 86400,
                hash: 'sha256',
                encs: ['aes256'],
                natTraversal: true,
                passiveMode: false,
                myIdType: 'auto',
                peerIdType: 'auto',
            },
            fase2: {
                mode: 'tunnel',
                localSubnets: [],
                remoteSubnets: [],
                pfs: 14,
                lifetime: 28800,
                hash: 'sha256',
                encs: ['aes256'],
            },
        });
        setFieldErrors({});
    };

    const handleEditIPSec = (ipsec: NetworkIPSecDTO) => {
        setIsEditModalOpen(true);
        setEditingType('ipsec');
        setEditingIPSec({ ...ipsec });
        setFieldErrors({});
    };

    const handleAddNetwork = () => {
        setIsEditModalOpen(true);
        setEditingType(null);
        setEditingNetwork(null);
        setEditingIPSec(null);
        setFieldErrors({});
    };

    React.useEffect(() => {
        onRegisterAdd?.(handleAddNetwork);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelectNetworkType = (type: 'lan' | 'wan' | 'ipsec') => {
        if (type === 'lan') {
            handleAddLan();
        } else if (type === 'wan') {
            handleAddWan();
        } else {
            handleAddIPSec();
        }
    };

    const handleEditLan = (network: NetworkLanDTO) => {
        setIsEditModalOpen(true);
        setEditingType('lan');
        setEditingNetwork({ ...network });
        setFieldErrors({});
    };

    const handleEditWan = (network: NetworkWanDTO) => {
        setIsEditModalOpen(true);
        setEditingType('wan');
        setEditingNetwork({ ...network });
        setFieldErrors({});
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setEditingType(null);
        setEditingNetwork(null);
        setEditingIPSec(null);
        setFieldErrors({});
    };

    const handleSave = () => {
        if (editingType === 'ipsec') {
            if (editingIPSec) mutSaveIPSec.mutate(editingIPSec);
            return;
        }
        if (!editingNetwork) return;
        if (editingType === 'lan') {
            mutSaveLan.mutate(editingNetwork as NetworkLanDTO);
        } else if (editingType === 'wan') {
            mutSaveWan.mutate(editingNetwork as NetworkWanDTO);
        }
    };

    const handleDeleteIPSec = (id: number) => {
        mutDeleteIPSec.mutate(id);
    };

    const noPortsDetected = !appliance.portas || appliance.portas.length === 0;

    // Redes que podem ser base (trunk) da LAN em edição: um nível só — a base
    // não tem base, e uma rede que já é base de outra não vira filha. OPNsense
    // não suporta (cada LAN precisa da própria interface atribuída).
    const editingLanId = editingType === 'lan' ? editingNetwork?.id : undefined;
    const editingEhBase = editingLanId != null
        && (appliance.redesLan ?? []).some(l => l.lan_base_id === editingLanId);
    const lanBases = appliance.agent_type === 'opnsense' || editingEhBase
        ? []
        : (appliance.redesLan ?? []).filter(l => !l.lan_base_id && l.id !== editingLanId);
    const nomeWan = (id?: number | null) => appliance.redesWan?.find(w => w.id === id)?.nome;
    const nomeLan = (id?: number | null) => appliance.redesLan?.find(l => l.id === id)?.nome ?? `#${id}`;

    return (
        <>
            <VBox style={{ gap: '24px', padding: '16px' }}>

                {noPortsDetected && (
                    <div style={{ padding: '12px 16px', background: 'var(--tc-background-color-warning-subtle)', border: '1px solid var(--tc-border-color-warning-subtle)', borderRadius: '6px', color: 'var(--tc-text-color-warning-emphasis)' }}>
                        Nenhuma porta detectada nesta appliance ainda. Isso é esperado se o agente configurado com o ID <strong>{appliance.agent_id}</strong> ainda não conectou. Conecte o agente e salve a appliance novamente antes de configurar as redes WAN/LAN.
                    </div>
                )}

                <VBox style={{ gap: '16px' }}>
                    <h3 style={{ marginBottom: '8px', fontSize: '18px', fontWeight: '600' }}>Redes LAN</h3>

                    {(!appliance.redesLan || appliance.redesLan.length === 0) && (
                        <p style={{ color: 'var(--color-muted)', fontStyle: 'italic', padding: '16px 0' }}>
                            Nenhuma rede LAN configurada
                        </p>
                    )}

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '16px'
                    }}>

                        {appliance.redesLan?.map(lan => {
                            const lanSt = lan.id != null ? lanStatusMap.get(lan.id) : undefined;
                            const lanStatusLabel = !lanSt ? 'Desconhecido'
                                : lanSt.status === 'online' ? 'Online'
                                    : lanSt.status === 'partial' ? 'Sem cabo'
                                        : lanSt.status === 'offline' ? 'Offline'
                                            : 'Desconhecido';
                            return (
                                <Card key={lan.id}>
                                    <Card.Header title={lan.nome}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 0' }}>
                                            <StatusDot status={lanSt?.status ?? 'unknown'} showLabel={false} title={lanStatusLabel} />
                                        </span>
                                        <Botao
                                            size="sm"
                                            variant="none-primary"
                                            icon={mdiPencil}
                                            onClick={() => handleEditLan(lan)}
                                        />
                                        <BotaoExcluirComCascata
                                            tipo="rede_lan"
                                            id={lan.id!}
                                            onExcluido={() => {
                                                toastSuccess('Rede LAN removida com sucesso!');
                                                onRefresh();
                                            }}
                                            onErro={(msg) => toastError(msg)}
                                        />
                                    </Card.Header>
                                    <Card.Body>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                <strong style={{ minWidth: '70px', color: 'var(--color-muted)', fontSize: '14px' }}>Tipo:</strong>
                                                <span style={{ flex: 1, fontSize: '14px' }}>{lan.tipoLan}{lan.tipoLan === 'estatico' && <span> - {lan.ips?.join(', ')}</span>}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                <strong style={{ minWidth: '70px', color: 'var(--color-muted)', fontSize: '14px' }}>IPs:</strong>
                                                <span style={{ flex: 1, fontSize: '14px' }}>{lan.ips?.join(', ') || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                <strong style={{ minWidth: '70px', color: 'var(--color-muted)', fontSize: '14px' }}>Portas:</strong>
                                                <span style={{ flex: 1, fontSize: '14px' }}>
                                                    {lan.lan_base_id
                                                        ? `mesmas de ${nomeLan(lan.lan_base_id)}`
                                                        : lan.portas?.join(', ') || '-'}
                                                </span>
                                            </div>
                                            {lan.wan_afinidade_id != null && nomeWan(lan.wan_afinidade_id) && (
                                                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                    <strong style={{ minWidth: '70px', color: 'var(--color-muted)', fontSize: '14px' }}>Link:</strong>
                                                    <span style={{ flex: 1, fontSize: '14px' }}>{nomeWan(lan.wan_afinidade_id)} (preferencial)</span>
                                                </div>
                                            )}
                                            {lan.vlan != null && lan.vlan > 0 && (
                                                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                    <strong style={{ minWidth: '70px', color: 'var(--color-muted)', fontSize: '14px' }}>VLAN:</strong>
                                                    <span style={{ flex: 1, fontSize: '14px' }}>{lan.vlan}</span>
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                <strong style={{ minWidth: '70px', color: 'var(--color-muted)', fontSize: '14px' }}>DHCP:</strong>
                                                <span style={{ flex: 1, fontSize: '14px' }}>{lan.dhcp_range != null ? lan.dhcp_range : 'Não'}</span>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            );
                        })}
                    </div>
                </VBox>

                <VBox style={{ gap: '16px' }}>
                    <h3 style={{ marginBottom: '8px', fontSize: '18px', fontWeight: '600' }}>Redes WAN</h3>

                    {(!appliance.redesWan || appliance.redesWan.length === 0) && (
                        <p style={{ color: 'var(--color-muted)', fontStyle: 'italic', padding: '16px 0' }}>
                            Nenhuma rede WAN configurada
                        </p>
                    )}

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '16px'
                    }}>

                        {appliance.redesWan?.map(wan => {
                            const wanSt = wan.id != null ? wanStatusMap.get(wan.id) : undefined;
                            const wanStatusLabel = !wanSt ? 'Desconhecido'
                                : wanSt.status === 'online' ? 'Online'
                                    : wanSt.status === 'offline' ? 'Offline'
                                        : wanSt.status === 'partial' ? 'Sem cabo'
                                            : 'Desconhecido';
                            return (
                                <Card key={wan.id}>
                                    <Card.Header title={wan.nome}>
                                        <HBox style={{ gap: '8px', alignItems: 'center' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 0' }}>
                                                <StatusDot status={wanSt?.status ?? 'unknown'} showLabel={false} title={wanStatusLabel} />
                                            </span>
                                            <Botao
                                                size="sm"
                                                variant="none-primary"
                                                icon={mdiPencil}
                                                onClick={() => handleEditWan(wan)}
                                            />
                                            <BotaoExcluirComCascata
                                                tipo="rede_wan"
                                                id={wan.id!}
                                                onExcluido={() => {
                                                    toastSuccess('Link WAN removido com sucesso!');
                                                    onRefresh();
                                                }}
                                                onErro={(msg) => toastError(msg)}
                                            />
                                        </HBox>
                                    </Card.Header>
                                    <Card.Body>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                <strong style={{ minWidth: '70px', color: 'var(--color-muted)', fontSize: '14px' }}>Tipo:</strong>
                                                <span style={{ flex: 1, fontSize: '14px' }}>{wan.tipoWan}{wan.tipoWan === 'estatico' && <span> - {wan.ips?.join(', ')}</span>}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                <strong style={{ minWidth: '70px', color: 'var(--color-muted)', fontSize: '14px' }}>Uso:</strong>
                                                <span style={{ flex: 1, fontSize: '14px' }}>{wan.uso}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                <strong style={{ minWidth: '70px', color: 'var(--color-muted)', fontSize: '14px' }}>Portas:</strong>
                                                <span style={{ flex: 1, fontSize: '14px' }}>{wan.portas?.join(', ') || '-'}</span>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            );
                        })}
                    </div>
                </VBox>

                <LoadBalancingPanel
                    primaries={(appliance.redesWan ?? []).filter(w => w.uso === 'primario')}
                    applianceId={appliance.id}
                    onSaved={onRefresh}
                />

                <VBox style={{ gap: '16px' }}>
                    <h3 style={{ marginBottom: '8px', fontSize: '18px', fontWeight: '600' }}>IPSec</h3>

                    {(!appliance.ipsecs || appliance.ipsecs.length === 0) && (
                        <p style={{ color: 'var(--color-muted)', fontStyle: 'italic', padding: '16px 0' }}>
                            Nenhum túnel IPSec configurado
                        </p>
                    )}

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '16px'
                    }}>
                        {appliance.ipsecs?.map(ipsec => {
                            const ipsecSt = ipsec.id != null ? ipsecStatusMap.get(ipsec.id) : undefined;
                            const ipsecStatusLabel = !ipsecSt ? 'Desconhecido'
                                : ipsecSt.status === 'online'
                                    ? (ipsecSt.uptime_seconds != null ? `Online há ${formatDuration(ipsecSt.uptime_seconds)}` : 'Online')
                                    : ipsecSt.status === 'standby' ? (ipsecSt.detail ?? 'Standby')
                                        : ipsecSt.status === 'partial' ? (ipsecSt.detail ?? 'Parcial (fase 2 falhou)')
                                            : ipsecSt.status === 'offline' ? 'Offline'
                                                : 'Desconhecido';
                            return (
                                <Card key={ipsec.id}>
                                    <Card.Header title={ipsec.nome}>
                                        <HBox style={{ gap: '8px' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 0' }}>
                                                <StatusDot status={ipsecSt?.status ?? 'unknown'} showLabel={false} title={ipsecStatusLabel} />
                                            </span>
                                            <Botao
                                                size="sm"
                                                variant="none-primary"
                                                icon={mdiPencil}
                                                onClick={() => handleEditIPSec(ipsec)}
                                            />
                                            <BotaoPerigoso
                                                size="sm"
                                                variant="none-danger"
                                                icon={mdiTrashCan}
                                                onConfirm={() => handleDeleteIPSec(ipsec.id!)}
                                            />
                                        </HBox>
                                    </Card.Header>
                                    <Card.Body>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                <strong style={{ minWidth: '80px', color: 'var(--color-muted)', fontSize: '14px' }}>Fase 1:</strong>
                                                <span style={{ flex: 1, fontSize: '14px' }}>{ipsec.fase1?.tipo?.toUpperCase()} / {ipsec.fase1?.auth}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                <strong style={{ minWidth: '80px', color: 'var(--color-muted)', fontSize: '14px' }}>Local:</strong>
                                                <span style={{ flex: 1, fontSize: '14px' }}>{ipsec.fase1?.enderecoLocal || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                <strong style={{ minWidth: '80px', color: 'var(--color-muted)', fontSize: '14px' }}>Remotos:</strong>
                                                <span style={{ flex: 1, fontSize: '14px' }}>{ipsec.fase1?.enderecosRemotos?.join(', ') || '-'}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                                <strong style={{ minWidth: '80px', color: 'var(--color-muted)', fontSize: '14px' }}>Fase 2:</strong>
                                                <span style={{ flex: 1, fontSize: '14px' }}>{ipsec.fase2?.mode}</span>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            );
                        })}
                    </div>

                    {appliance.id != null && (appliance.ipsecs?.length ?? 0) > 0 && (
                        <ConnectionLogPanel
                            applianceId={appliance.id}
                            topics={['ipsec']}
                            title="Logs de IPSec"
                        />
                    )}
                </VBox>
            </VBox>

            <Modal
                show={isEditModalOpen}
                title={
                    !editingType
                        ? 'Nova Rede'
                        : editingType === 'lan'
                            ? (editingNetwork?.id && editingNetwork.id > 0 ? 'Editar Rede LAN' : 'Nova Rede LAN')
                            : editingType === 'wan'
                                ? (editingNetwork?.id && editingNetwork.id > 0 ? 'Editar Rede WAN' : 'Nova Rede WAN')
                                : (editingIPSec?.id && editingIPSec.id > 0 ? 'Editar IPSec' : 'Novo IPSec')
                }
                onClose={handleCloseEditModal}
                onCancel={handleCloseEditModal}
                onConfirm={handleSave}
                confirmProps={{
                    label: 'Salvar',
                    disabled: mutSaveLan.isLoading || mutSaveWan.isLoading || mutSaveIPSec.isLoading || !editingType,
                }}
                size="md"
            >
                {editingType === 'ipsec' ? (
                    <FormIPSec
                        value={editingIPSec!}
                        onChange={setEditingIPSec}
                        fieldErrors={fieldErrors}
                    />
                ) : (
                    <FormNetwork
                        networkType={editingType as 'lan' | 'wan' | null}
                        onChangeNetworkType={handleSelectNetworkType}
                        value={editingNetwork}
                        onChange={(v) => setEditingNetwork(v)}
                        fieldErrors={fieldErrors}
                        availablePorts={availablePorts}
                        applianceId={appliance.id}
                        lanBases={lanBases}
                        wans={appliance.agent_type === 'opnsense' ? undefined : appliance.redesWan ?? []}
                    />
                )}
            </Modal>
        </>
    );
};
